import React, { useState } from "react";
import { Trash2, Wand2, Images, ImageUp, Sparkles, AlertTriangle } from "lucide-react";
import { useImageStudio } from "../../context/ImageStudioContext";
import { StudioAsset } from "../../service/apiService";

interface AssetPanelProps {
  onView: (asset: StudioAsset) => void;
}

const AssetPanel: React.FC<AssetPanelProps> = ({ onView }) => {
  const { assets, loadingAssets, loadAsInput, deleteItem } = useImageStudio();
  const [pendingDelete, setPendingDelete] = useState<StudioAsset | null>(null);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteItem(pendingDelete.asset_id);
    setPendingDelete(null);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-border-default px-4 py-3">
        <Images size={16} className="text-muted" />
        <h3 className="text-sm font-semibold text-primary">Assets</h3>
        {assets.length > 0 && (
          <span className="ml-auto text-xs text-subtle">{assets.length}</span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {loadingAssets && assets.length === 0 ? (
          <p className="px-1 py-4 text-xs text-subtle">Loading…</p>
        ) : assets.length === 0 ? (
          <p className="px-1 py-4 text-xs text-subtle">
            Generated images and uploads in this gallery show up here.
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-2">
            {assets.map((asset) => (
              <div
                key={asset.asset_id}
                className="group relative overflow-hidden rounded-md border border-border-default bg-surface-2"
              >
                <button
                  type="button"
                  onClick={() => onView(asset)}
                  className="block w-full"
                  title={asset.prompt || asset.file_name || asset.tool || asset.asset_type}
                >
                  <img
                    src={asset.presigned_url}
                    alt={asset.prompt || asset.file_name || asset.asset_type}
                    className="aspect-square w-full object-cover"
                  />
                </button>

                {/* type badge — uploaded (original) vs generated (AI) */}
                <span className="absolute left-1.5 top-1.5 flex items-center gap-0.5 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  {asset.asset_type === "uploaded" ? (
                    <>
                      <ImageUp size={10} /> Uploaded
                    </>
                  ) : (
                    <>
                      <Sparkles size={10} /> Generated
                    </>
                  )}
                </span>

                <div className="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 bg-gradient-to-t from-black/60 to-transparent p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => loadAsInput(asset)}
                    title="Use as input"
                    className="rounded bg-white/90 p-1 text-zinc-700 hover:bg-white"
                  >
                    <Wand2 size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingDelete(asset)}
                    title="Delete"
                    className="rounded bg-white/90 p-1 text-red-600 hover:bg-white"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPendingDelete(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-sm rounded-lg border border-border-default bg-surface-overlay shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 p-5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-red-500">
                <AlertTriangle size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-primary">Delete image</h2>
                <p className="mt-1 text-sm text-muted">
                  Delete this {pendingDelete.asset_type === "uploaded" ? "uploaded" : "generated"}{" "}
                  image from the gallery? This action cannot be undone.
                </p>
                <img
                  src={pendingDelete.presigned_url}
                  alt={pendingDelete.prompt || pendingDelete.file_name || "asset to delete"}
                  className="mt-3 max-h-28 rounded-md border border-border-default object-contain"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-border-default px-5 py-3">
              <button
                onClick={() => setPendingDelete(null)}
                className="rounded-md px-3 py-1.5 text-sm font-medium text-muted hover:bg-surface-2 hover:text-primary"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetPanel;
